import { createTheme } from "@mui/material/styles";

import GothamBold from "./fonts/GothamBold.ttf";
import GothamBook from "./fonts/GothamBook.ttf";
import GothamLight from "./fonts/GothamLight.ttf";
import GothamMedium from "./fonts/GothamMedium.ttf";
import RecifeDisplayTrialBlack from "./fonts/RecifeDisplayTrialBlack.ttf";
import RecifeDisplayTrialBold from "./fonts/RecifeDisplayTrialBold.ttf";
import RecifeDisplayTrialMedium from "./fonts/RecifeDisplayTrialMedium.ttf";
import RecifeDisplayTrialRegular from "./fonts/RecifeDisplayTrialRegular.ttf";

const createBoxShadow = (mode) => {
  if (mode === "light") {
    return "3px 3px 6px rgba(0, 0, 0, 0.1), -3px -3px 6px rgba(255, 255, 255, 0.7)";
  } else {
    // For dark mode, you can adjust the shadow values as needed.
    // Here, we are making the shadow slightly darker.
    //return "3px 3px 6px rgba(255, 255, 255, 0.1), -3px -3px 6px rgba(0, 0, 0, 0.3)";
    return "none";
  }
};

const FairtreeTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      ...(mode === "light"
        ? {
            primary: {
              main: "#CC6323",
            },
            secondary: {
              main: "#424242",
            },
            background: {
              default: "#F8F8F8",
              paper: "#EAE8E4",
            },
          }
        : {
            primary: {
              main: "#CC6323",
            },
            secondary: {
              main: "#707070",
            },
            background: {
              default: "#231F20",
              paper: "#2E2A2B",
            },
          }),
    },
    typography: {
      fontFamily: "Gotham, Arial",
      htmlFontSize: 18,
      body1: {
        fontSize: "0.8rem",
      },
      subtitle1: {
        fontSize: "0.65rem",
      },
      tableHeading: {
        fontSize: "0.8rem",
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: "0.9rem",
        fontWeight: 500,
      },
      h1: {
        fontSize: "2.5rem",
      },
      h2: {
        fontSize: "2.0rem",
      },
      h3: {
        fontSize: "1.7rem",
      },
      h4: {
        fontSize: "1.4rem",
      },
      h5: {
        fontSize: "1rem",
      },
      h6: {
        fontSize: "0.9rem",
        fontWeight: 400,
      },
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: {
            border: "none",
            boxShadow: createBoxShadow(mode),
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            border: "none",
            boxShadow: createBoxShadow(mode),
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            boxShadow: "none", // Remove boxShadow from dialogs
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          enableColorOnDark: true,
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
        @font-face {
          font-family: 'Gotham';
          font-style: normal;
          font-display: swap;
          font-weight: 200;
          src: local('Gotham Light'), local('Gotham-Light'), url(${GothamLight}) format('truetype');
        unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;        
        }

        @font-face {
          font-family: 'Gotham';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Gotham Book'), local('Gotham-Book'), url(${GothamBook}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }

        @font-face {
          font-family: 'Gotham';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: local('Gotham Medium'), local('Gotham-Medium'), url(${GothamMedium}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }

        @font-face {
          font-family: 'Gotham';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: local('Gotham Bold'), local('Gotham-Bold'), url(${GothamBold}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }

        @font-face {
          font-family: 'RecifeDisplayTrial';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Recife Display Trial'), local('RecifeDisplayTrial-Regular'), url(${RecifeDisplayTrialRegular}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }

        @font-face {
          font-family: 'RecifeDisplayTrial';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: local('Recife Display Trial Black'), local('RecifeDisplayTrial-Black'), url(${RecifeDisplayTrialBlack}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }

        @font-face {
          font-family: 'RecifeDisplayTrial';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: local('Recife Display Trial Medium'), local('RecifeDisplayTrial-Medium'), url(${RecifeDisplayTrialMedium}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }

        @font-face {
          font-family: 'RecifeDisplayTrial';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: local('Recife Display Trial'), local('RecifeDisplayTrial-Bold'), url(${RecifeDisplayTrialBold}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
      `,
      },
    },
  });

export { FairtreeTheme };
